import * as qs from 'qs';
import { useMemo } from 'react';

import { useReportOptionsParams } from '../../../components/reportsv3/hooks/useReportOptionsParams';
import { isReleaseGroupScope, type ReportsV3Params, type ReportV3Format } from '../../../components/reportsv3/types';
import { useParams } from '../../../contexts/RouterContext';
import { useSelectedReleaseIdForRoute } from '../../../redux/project_grouping/hooks';
import { nuc } from '../../../utils';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { get } from '../../../redux/api';

/**
 * Hook that returns a mutation to email a report to the given email addresses.
 * Constructs the appropriate URL based on scope (project vs release group).
 *
 * @param {ReportV3Format} format The report format.
 */
export function useEmailReport(format: ReportV3Format) {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const selectedReleaseId = useSelectedReleaseIdForRoute();
  const reportOptionsParams = useReportOptionsParams();

  const baseUrl = useMemo(() => {
    if (isReleaseGroup) {
      const { projectGroupId, releaseId } = params;
      const resolvedReleaseId = releaseId ?? selectedReleaseId ?? 0;
      return `/api/v2/project_group/${projectGroupId}/release/${resolvedReleaseId}/attribution/${format}`;
    }
    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api/v2/revisions/${projectLocator}/attribution/email`;
  }, [format, isReleaseGroup, params, selectedReleaseId]);

  return useToastedMutation({
    mutationFn: (emails: string) => get(baseUrl, false, {
      ...qs.parse(reportOptionsParams),
      emails,
      format,
    }),
    successToastMessage: 'Your email request is being processed. You will receive an email shortly.',
    errorToastMessage: 'Failed to send email',
  });
}
